// dependencies
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useIntl } from "react-intl";
import { css, StyleSheet } from "aphrodite";
import PropTypes from "prop-types";

// components
import {
  IconCaretUpO,
  IconCaretDownO,
} from "@cloudspire/legacy-svg-icon-library";
import Text from "../../../components/Text";
import Stack from "../../../components/Stack";
import {
  List,
  ListItem,
} from "@cloudspire/legacy-resources/src/components/List";
import ActivityIndicator from "../../../components/ActivityIndicator";
import { useHighlightOverlay } from "../../../components/HighlightOverlay";

// containers
import ReserveButtonContainer from "./ReserveButton";
import { RoomAside as RoomAsideContainer } from "../../../containers/Room";
import BookingAsideDetails from "./Details";
import BookingAsidePrice from "./Price";
import BookingAsideActions from "./Actions";
import BookingAsideForm from "./Form";

// contexts
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";

// helpers
import {
  roomIsBookableOnline,
  roomIsStateEnabled,
} from "../../../helpers/room";

const styles = StyleSheet.create({
  createBookingButton: {
    paddingTop: "0.3125rem",
    paddingBottom: "0.3125rem",
  },
  paneClosed: {
    height: 0,
    opacity: 0,
    overflow: "hidden",
  },
});

function ResponsiveAside(props) {
  const {
    errorMessage,
    priceToRender,
    hasBookingEstimateList,
    hasBookingEstimateResume,
    hasBookingEstimatePrice,
    hasBookingEstimateAdvance,
    hasPricingEstimatePrice,
    type,
    list,
    resume,
    bookingEstimatePrice,
    pricingEstimatePrice,
    advance,
    total,
    room,
    bookingEstimateResponse,
    pricingEstimateResponse,
    parameterList,
    bookingReference,
    shouldRenderLegend,
    shouldRenderBookingDetailButton,
    onChange,
    onPressContactUs,
    onPressOwnerContactMail,
    onPressOwnerContactPhone,
    onPressContactPhone,
    onPressContactMail,
  } = props;

  const [opened, setOpened] = useState(false);

  const highlightOverlay = useHighlightOverlay();

  const configuration = useConfiguration();

  useEffect(
    function () {
      function handleClick(event) {
        if (event.target === highlightOverlay?.overlayRef?.current) {
          setOpened(false);
        }
      }
      if (highlightOverlay?.highlighted) {
        document.body.addEventListener("click", handleClick, { capture: true });
      }
      return function () {
        document.body.removeEventListener("click", handleClick, {
          capture: true,
        });
      };
    },
    [highlightOverlay?.overlayRef, highlightOverlay?.highlighted]
  );

  useEffect(
    function () {
      if (highlightOverlay && highlightOverlay?.highlighted !== opened) {
        highlightOverlay.setHighlighted(opened);
      }
    },
    [opened, highlightOverlay]
  );

  const intl = useIntl();

  const bookingEstimateLoading =
    (!bookingEstimateResponse?.data && !bookingEstimateResponse?.error) ||
    bookingEstimateResponse?.isValidating;
  const pricingEstimateLoading =
    (!pricingEstimateResponse?.error && !pricingEstimateResponse?.data) ||
    pricingEstimateResponse?.isValidating;

  const hasCreate =
    "room" === type &&
    ("booking" === priceToRender
      ? Boolean(bookingEstimateResponse?.data?.data)
      : Boolean(pricingEstimateResponse?.data?.data) &&
        hasPricingEstimatePrice);

  const shouldRenderBookingButton =
    hasCreate &&
    roomIsBookableOnline({ room, configuration }) &&
    !bookingEstimateLoading;

  /**
   * Gère le clic sur le prix (pour afficher plus de détails sur le prix).
   */
  const handlePressOpen = useCallback(() => {
    setOpened(!opened);
  }, [opened]);

  /**
   * Gère le clic sur le bouton de réservation
   */
  function handlePressBooking(event) {
    event.stopPropagation();
    if (!parameterList?.checkin || !parameterList?.checkout) {
      if (!opened) {
        setOpened(true);
      }
      event.preventDefault();
      $inputCheckinRef.current?.focus?.();
    }
  }

  const $inputCheckinRef = useRef();

  return (
    <>
      {!roomIsStateEnabled({ room }) ? (
        <Text>
          {intl.formatMessage({
            defaultMessage:
              "Hébergement non réservable pour une durée indéterminée",
          })}
        </Text>
      ) : (
        <>
          <div className={css(!opened && styles.paneClosed)}>
            <BookingAsideForm
              inputCheckinRef={$inputCheckinRef}
              type={type}
              room={room}
              parameterList={parameterList}
              shouldRenderLegend={shouldRenderLegend}
              onChange={onChange}
            />

            {("booking" === type || "payment" === type) && (
              <RoomAsideContainer room={room} />
            )}

            <BookingAsideDetails
              mode="responsive"
              priceToRender={priceToRender}
              hasBookingEstimateList={hasBookingEstimateList}
              hasBookingEstimateResume={hasBookingEstimateResume}
              hasBookingEstimatePrice={hasBookingEstimatePrice}
              hasBookingEstimateAdvance={hasBookingEstimateAdvance}
              hasPricingEstimatePrice={hasPricingEstimatePrice}
              type={type}
              list={list}
              total={total}
              resume={resume}
              bookingEstimatePrice={bookingEstimatePrice}
              pricingEstimatePrice={pricingEstimatePrice}
              advance={advance}
              pricingEstimateResponse={pricingEstimateResponse}
              bookingEstimateResponse={bookingEstimateResponse}
              shouldRenderBookingDetailButton={shouldRenderBookingDetailButton}
              bookingReference={bookingReference}
            />

            <Stack marginTop={1} />

            <BookingAsideActions
              mode="responsive"
              hasPricingEstimatePrice={hasPricingEstimatePrice}
              hasBookingEstimatePrice={hasBookingEstimatePrice}
              parameterList={parameterList}
              priceToRender={priceToRender}
              room={room}
              type={type}
              pricingEstimateResponse={pricingEstimateResponse}
              bookingEstimateResponse={bookingEstimateResponse}
              onPressContactUs={onPressContactUs}
              onPressOwnerContactMail={onPressOwnerContactMail}
              onPressOwnerContactPhone={onPressOwnerContactPhone}
              onPressContactPhone={onPressContactPhone}
              onPressContactMail={onPressContactMail}
            />

            <Stack marginTop={1} />
          </div>

          <Text onPress={handlePressOpen}>
            <List gutter={1} inline={true}>
              <ListItem style={{ justifyContent: "center" }}>
                {opened ? <IconCaretDownO /> : <IconCaretUpO />}
              </ListItem>

              {bookingEstimateLoading && (
                <ListItem>
                  <ActivityIndicator />
                </ListItem>
              )}

              <ListItem style={{ flex: 1 }}>
                {(pricingEstimateResponse?.error ||
                  bookingEstimateResponse?.error) &&
                errorMessage ? (
                  <Text>{errorMessage}</Text>
                ) : (
                  <>
                    {pricingEstimateLoading ? (
                      <ActivityIndicator />
                    ) : (
                      <BookingAsidePrice
                        mode="responsive"
                        hasBookingEstimatePrice={hasBookingEstimatePrice}
                        bookingEstimatePrice={bookingEstimatePrice}
                        hasPricingEstimatePrice={hasPricingEstimatePrice}
                        priceToRender={priceToRender}
                        pricingEstimatePrice={pricingEstimatePrice}
                        type={type}
                      />
                    )}
                  </>
                )}
              </ListItem>
            </List>

            {shouldRenderBookingButton && (
              <div>
                <Stack marginTop={1} />

                <ReserveButtonContainer
                  room={room}
                  aStyle={styles.createBookingButton}
                  parameterList={parameterList}
                  onPressBooking={handlePressBooking}
                />
              </div>
            )}
          </Text>
        </>
      )}
    </>
  );
}

ResponsiveAside.propTypes = {
  errorMessage: PropTypes.string,
  type: PropTypes.oneOf(["booking", "room", "payment"]),
  priceToRender: PropTypes.oneOf(["booking", "pricing"]),
  hasBookingEstimateList: PropTypes.bool,
  hasBookingEstimateResume: PropTypes.bool,
  hasBookingEstimatePrice: PropTypes.bool,
  hasBookingEstimateAdvance: PropTypes.bool,
  hasPricingEstimatePrice: PropTypes.bool,
  list: PropTypes.any,
  total: PropTypes.any,
  resume: PropTypes.any,
  bookingEstimatePrice: PropTypes.any,
  pricingEstimatePrice: PropTypes.any,
  advance: PropTypes.any,
  room: PropTypes.object,
  parameterList: PropTypes.any,
  shouldRenderLegend: PropTypes.bool,
  onChange: PropTypes.func,
  onPressContactUs: PropTypes.func,
  onPressOwnerContactMail: PropTypes.func,
  onPressOwnerContactPhone: PropTypes.func,
  onPressContactPhone: PropTypes.func,
  onPressContactMail: PropTypes.func,
};

export default ResponsiveAside;
